"use client";

import { Button } from "@universe/ui/components/button";
import { format } from "date-fns";
import { useTranslations } from "next-intl";
import React from "react";

import BlockchainNetworkIcon from "@/components/blockchain-network-icon";
import CollapsibleDetailSection from "@/components/collapsible-detail-section";
import DetailRow from "@/components/detail-row";
import FormattedCurrencyAmount from "@/components/formatted-currency-amount";
import { LEDGER_ENTRY_DATE_TIME_LONG_FORMAT } from "@/constants";
import { WITHDRAWAL_REASON_WITHDRAWAL_REVERSAL } from "@/constants/ledgerReasons";
import { getBlockchainBeneficiaryName } from "@/utils/getLegalEntityName";
import {
  FilteredResolvedBlockchainBeneficiary,
  InstructionState,
} from "@generated/types";

import InstructionStatusBadge from "../instruction-status-badge";
import TransactionStatusBadge, {
  TransactionStatusBadgeType,
} from "../transaction-status-badge";

import LedgerEntryBaseFields from "./ledger-entry-base-fields";
import { MissingResolutionAlert } from "./missing-resolution-alert";

const PROCESSING_STATES = [
  "ACCEPTED",
  "WAITING",
  "READY",
  "PROCESSING",
  "PENDING",
  "UNRECONCILED",
  "SCHEDULED",
  "FUNDS_CONFIRMED",
] as const;

interface BlockchainWithdrawalsDetailsProps {
  entry: LedgerEntryResolvedBlockchainWithdrawal;
  showInstructionStatus?: boolean;
  amountLabel?: string;
  statusOverride?: TransactionStatusBadgeType;
}

export default function BlockchainWithdrawalsDetails({
  entry,
  showInstructionStatus = false,
  amountLabel,
  statusOverride,
}: BlockchainWithdrawalsDetailsProps) {
  const tCommon = useTranslations("common");
  const tLedgerFields = useTranslations("ledger.fields");
  const tWallet = useTranslations("wallet");
  const tPay = useTranslations("pay");

  if (!entry) return null;

  // BFF resolution failed (graceful degradation). Render base ledger fields
  // plus a support prompt — the dot-walks below would throw on null.
  if (!entry.resolvedData) {
    return (
      <div className="space-y-0">
        <LedgerEntryBaseFields entry={entry} />
        <MissingResolutionAlert />
      </div>
    );
  }

  const isWithdrawalReversal =
    entry.reason === WITHDRAWAL_REASON_WITHDRAWAL_REVERSAL;

  const isFee = entry.reason === "fee";

  // Narrow beneficiary: string means UUID fallback (resolution failed)
  const beneficiary =
    typeof entry.resolvedData.beneficiary === "string"
      ? undefined
      : entry.resolvedData.beneficiary;

  const displayedStatus = statusOverride
    ? statusOverride
    : showInstructionStatus &&
        "instructionState" in entry.resolvedData &&
        entry.resolvedData.instructionState
      ? entry.resolvedData.instructionState === InstructionState.Unconfirmed
        ? "PROCESSING"
        : null
      : isWithdrawalReversal
        ? "RETURNED"
        : entry.resolvedData.withdrawalState;

  const isProcessing =
    !!displayedStatus &&
    PROCESSING_STATES.includes(
      displayedStatus as (typeof PROCESSING_STATES)[number]
    );

  return (
    <div className="space-y-0">
      <DetailRow
        label={tCommon("to")}
        value={String(
          beneficiary
            ? getBlockchainBeneficiaryName(
                beneficiary as FilteredResolvedBlockchainBeneficiary
              )
            : ""
        )}
        translateValue={false}
      />
      {isWithdrawalReversal ? (
        <DetailRow
          label={tLedgerFields("amountReturned")}
          value={
            <FormattedCurrencyAmount
              balance={entry.balance ?? 0}
              product={entry.product}
            />
          }
          translateValue={false}
        />
      ) : (
        <DetailRow
          label={
            amountLabel ||
            tLedgerFields(isFee ? "transactionFee" : "amountSent")
          }
          value={
            <FormattedCurrencyAmount
              balance={entry.balance ?? 0}
              product={entry.product}
            />
          }
          translateValue={false}
        />
      )}

      {showInstructionStatus &&
        !isWithdrawalReversal &&
        !isFee &&
        "fee" in entry.resolvedData &&
        entry.resolvedData.fee?.product && (
          <DetailRow
            label={tLedgerFields("transactionFee")}
            value={
              <FormattedCurrencyAmount
                balance={entry.resolvedData.fee?.amount ?? 0}
                product={entry.resolvedData.fee.product}
                forceNegative
              />
            }
            translateValue={false}
          />
        )}

      <hr className="my-2 opacity-65" />

      <DetailRow
        label={tLedgerFields("sentOn")}
        value={format(
          new Date(entry.resolvedData.meta?.createTime || ""),
          LEDGER_ENTRY_DATE_TIME_LONG_FORMAT
        )}
      />

      <DetailRow
        label={tLedgerFields("status")}
        value={
          statusOverride ? (
            <TransactionStatusBadge size="xs" status={statusOverride} />
          ) : showInstructionStatus &&
            "instructionState" in entry.resolvedData &&
            entry.resolvedData.instructionState ? (
            <InstructionStatusBadge
              size="xs"
              status={entry.resolvedData.instructionState}
            />
          ) : (
            <TransactionStatusBadge
              status={
                entry.resolvedData.withdrawalState as TransactionStatusBadgeType
              }
              isReturned={isWithdrawalReversal}
              size="xs"
            />
          )
        }
      />

      <hr className="my-2 opacity-65" />

      <CollapsibleDetailSection
        title={tWallet("header")}
        showLabel={tWallet("viewDetails")}
        hideLabel={tWallet("hideDetails")}
      >
        <div className="space-y-0">
          <DetailRow
            label={tLedgerFields("network")}
            value={
              <div className="flex items-center gap-2">
                <BlockchainNetworkIcon
                  size="24px"
                  network={beneficiary?.wallet?.blockchain?.id ?? ""}
                />
                <span>{beneficiary?.wallet?.blockchain?.name}</span>
              </div>
            }
            stack
            translateValue={false}
          />

          <DetailRow
            label={tLedgerFields("destinationAddress")}
            textClassName="w-full bg-brand-primary/5 rounded-lg font-mono p-3 text-xs lg:text-sm"
            value={beneficiary?.wallet?.address}
            stack
            copyable
            translateValue={false}
          />

          <Button variant="outline" className="w-full mb-2 my-2" asChild>
            <a
              href={beneficiary?.wallet?.blockchain?.explorer?.addressUrl?.replace(
                "%s",
                beneficiary?.wallet?.address ?? ""
              )}
              target="_blank"
              rel="noopener noreferrer"
            >
              {tCommon("viewWalletOnExplorer")}
            </a>
          </Button>
        </div>
      </CollapsibleDetailSection>

      <hr className="my-2 opacity-65" />

      <DetailRow
        label={tLedgerFields("referenceId")}
        value={
          "reference" in entry.resolvedData
            ? (entry.resolvedData.reference as string)
            : undefined
        }
        translateValue={false}
      />

      {isProcessing && (
        <div className="w-full mt-4 rounded-lg border border-brand-primary/30 bg-brand-primary/5 px-4 py-3 text-center text-xs font-medium text-gray-900">
          {tPay("paymentTimings")}
        </div>
      )}
    </div>
  );
}
